import React, { useEffect, useState } from 'react';
import { X, Check } from 'lucide-react';
import { apiClient } from '../services/api';
import { KeywordFields } from './KeywordFields';
import {
  BIZ_DIVS,
  CLASSIFICATIONS,
  CRON_PRESETS,
  Schedule,
  ScheduleForm,
  emptyForm,
} from '../types/schedule';
import { scheduleApi } from '../mocks/scheduleApi';

const SEL = 'rounded-lg border border-slate-300 px-2.5 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500';

/** 고를 수 있는 수신자 — GET /employees/recipients */
interface Recipient {
  id: number;
  name: string;
  email: string;
  department: string;
}

/** 미리보기 — /bidding/search 응답에서 쓰는 만큼만 */
interface PreviewResult {
  total: number;
  items: { bidNtceNo: string; bidNtceNm: string }[];
}

interface Props {
  open: boolean;
  /** 고칠 기준. null 이면 새로 만든다 */
  editing?: Schedule | null;
  /** 공고 화면에서 넘기는 초기값 — 검색창에 잡아둔 조건 */
  initial?: Partial<ScheduleForm>;
  onClose: () => void;
  onSaved: () => void;
}

function toForm(schedule: Schedule): ScheduleForm {
  return {
    name: schedule.name,
    keywords: schedule.keywords || '',
    institution: schedule.institution || '',
    cronExpression: schedule.cronExpression,
    emailTo: schedule.emailTo,
    excludeKeywords: schedule.excludeKeywords || '',
    bizDiv: schedule.bizDiv || '용역',
    classification: schedule.classification || '',
    minPrice: schedule.minPrice ?? null,
    maxPrice: schedule.maxPrice ?? null,
    license: schedule.license || '',
  };
}

export const ScheduleFormModal: React.FC<Props> = ({
  open,
  editing,
  initial,
  onClose,
  onSaved,
}) => {
  const [form, setForm] = useState<ScheduleForm>(emptyForm);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [preview, setPreview] = useState<PreviewResult | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [recipients, setRecipients] = useState<Recipient[]>([]);

  // 열릴 때마다 처음부터 — 닫고 다시 연 팝업에 지난 입력이 남지 않게 한다
  useEffect(() => {
    if (!open) return;
    setForm(editing ? toForm(editing) : { ...emptyForm, ...initial });
    setPreview(null);
    setError('');
    // initial 은 부모가 매 렌더 새로 만드는 객체라 의존성에 넣지 않는다
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, editing]);

  // 수신자 후보. 목록을 못 가져와도 직접 입력으로 계속 쓸 수 있게 조용히 넘어간다
  useEffect(() => {
    if (!open || recipients.length > 0) return;
    apiClient
      .get('/employees/recipients')
      .then(setRecipients)
      .catch(() => setRecipients([]));
  }, [open, recipients.length]);

  if (!open) return null;

  /** emailTo 는 지금도 쉼표로 여러 명을 담는다 — 저장 형식은 그대로 두고 고르는 방법만 더한다 */
  const selected = form.emailTo
    .split(',')
    .map((e) => e.trim())
    .filter(Boolean);

  const toggleRecipient = (email: string) => {
    const next = selected.includes(email)
      ? selected.filter((e) => e !== email)
      : [...selected, email];
    setForm({ ...form, emailTo: next.join(', ') });
  };

  /* 지금 입력된 기준으로 실제 조회해 본다. 저장 전에 확인하는 용도라
     스케줄을 만들지 않고 공고 검색 API 를 그대로 쓴다. */
  const runPreview = async () => {
    setPreviewLoading(true);
    setPreview(null);
    try {
      // 공고 화면 「게시일 전체」와 같은 조건 — 테이블과 같은 숫자가 나온다(#152)
      const params = new URLSearchParams();
      params.set('page', '1');
      params.set('pageSize', '10');
      if (form.keywords.trim()) params.set('keyword', form.keywords.trim());
      if (form.institution.trim()) params.set('institution', form.institution.trim());
      // 알림이 실제로 거는 조건 전부 — 전엔 키워드 · 기관만 보내 건수가 실제 알림과 달랐다(#111)
      if (form.excludeKeywords.trim()) params.set('excludeKeyword', form.excludeKeywords.trim());
      if (form.bizDiv) params.set('bizDiv', form.bizDiv);
      if (form.classification) params.set('classification', form.classification);
      if (form.minPrice != null) params.set('minPrice', String(form.minPrice));
      if (form.maxPrice != null) params.set('maxPrice', String(form.maxPrice));

      const data = await apiClient.get(`/bidding/search?${params.toString()}`);
      setPreview(data);
    } catch (err: any) {
      setError(err.message || '확인 중 오류가 발생했습니다.');
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleSave = async () => {
    if (!form.name.trim() || !form.emailTo.trim()) {
      setError('이름과 수신자 이메일은 필수입니다.');
      return;
    }
    setSaving(true);
    setError('');
    try {
      const payload: ScheduleForm = {
        ...form,
        name: form.name.trim(),
        keywords: form.keywords.trim(),
        institution: form.institution.trim(),
        excludeKeywords: form.excludeKeywords.trim(),
        // 화면 조회에 면허 조건이 없다 — 알림도 같게 비운다(#152)
        license: '',
        emailTo: form.emailTo.trim(),
      };
      if (editing) {
        await scheduleApi.update(editing.id, payload);
      } else {
        await scheduleApi.create(payload);
      }
      onSaved();
      onClose();
    } catch (err: any) {
      setError(err.message || '저장에 실패했습니다.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4"
      /* 바깥을 눌러도 닫지 않는다 — 쓰던 조건이 사라졌다(#109). 「취소」 · ✕ 로 닫는다 */
    >
      <div
        className="bg-white rounded-2xl shadow-xl w-full max-w-lg max-h-[90vh] flex flex-col overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between shrink-0">
          <h2 className="text-lg font-bold text-slate-900">
            {editing ? '수집 기준 수정' : '수집 기준 추가'}
          </h2>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600">
            <X size={20} />
          </button>
        </div>

        {/* 본문만 스크롤한다 — 상자 전체가 스크롤되면 스크롤바가 둥근 모서리 · 머리 · 바닥까지 걸치고 바닥 뒤로 글이 숨는다 */}
        <div className="px-6 py-5 space-y-4 flex-1 min-h-0 overflow-y-auto">
          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
              {error}
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">수집명 *</label>
            <input
              type="text"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
              placeholder="예: NIPA 모니터링"
            />
          </div>

          {/* 조건 — 공고 화면 조회 조건과 같은 순서 · 모양(#152) */}
          <div className="space-y-2">
            <label className="block text-sm font-medium text-slate-700">조건</label>
            <div className="flex flex-wrap items-center gap-2">
              <select
                aria-label="업무구분"
                value={form.bizDiv}
                onChange={(e) =>
                  setForm({ ...form, bizDiv: e.target.value, classification: e.target.value === '용역' ? form.classification : '' })
                }
                className={SEL}
              >
                {BIZ_DIVS.map((b) => (
                  <option key={b.value} value={b.value}>{b.label}</option>
                ))}
              </select>
              <select
                aria-label="분류"
                value={form.classification}
                disabled={form.bizDiv !== '용역'}
                onChange={(e) => setForm({ ...form, classification: e.target.value })}
                className={`${SEL} max-w-[11rem] disabled:bg-slate-50 disabled:text-slate-300`}
              >
                <option value="">분류 전체</option>
                {CLASSIFICATIONS.map((c) => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
              <input
                type="text"
                aria-label="기관명"
                value={form.institution}
                onChange={(e) => setForm({ ...form, institution: e.target.value })}
                placeholder="기관명"
                className={`${SEL} min-w-0 flex-1`}
              />
            </div>
            <KeywordFields
              value={{ keyword: form.keywords, exclude: form.excludeKeywords }}
              onChange={(v) => setForm((f) => ({ ...f, keywords: v.keyword, excludeKeywords: v.exclude }))}
            />
            <div className="flex items-center gap-3">
              <span className="w-14 flex-shrink-0 text-xs font-medium text-slate-500">추정가격</span>
              <input
                type="number"
                min={0}
                aria-label="추정가격 최소"
                value={form.minPrice ?? ''}
                onChange={(e) => setForm({ ...form, minPrice: e.target.value === '' ? null : Number(e.target.value) })}
                placeholder="최소"
                className={`${SEL} w-32`}
              />
              <span className="text-slate-400">~</span>
              <input
                type="number"
                min={0}
                aria-label="추정가격 최대"
                value={form.maxPrice ?? ''}
                onChange={(e) => setForm({ ...form, maxPrice: e.target.value === '' ? null : Number(e.target.value) })}
                placeholder="최대"
                className={`${SEL} w-32`}
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">실행 주기</label>
            <select
              value={form.cronExpression}
              onChange={(e) => setForm({ ...form, cronExpression: e.target.value })}
              className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
            >
              {CRON_PRESETS.map((p) => (
                <option key={p.value} value={p.value}>{p.label}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              수신자 *
            </label>

            {recipients.length > 0 && (
              <div className="mb-2 flex flex-wrap gap-1.5">
                {recipients.map((r) => {
                  const on = selected.includes(r.email);
                  return (
                    <button
                      key={r.id}
                      type="button"
                      onClick={() => toggleRecipient(r.email)}
                      title={`${r.department} · ${r.email}`}
                      className={`px-2.5 py-1 rounded-full text-xs font-medium border transition flex items-center gap-1 ${
                        on
                          ? 'bg-indigo-50 border-indigo-200 text-indigo-600'
                          : 'bg-white border-slate-200 text-slate-500 hover:bg-slate-50'
                      }`}
                    >
                      {on && <Check size={11} />}
                      {r.name}
                    </button>
                  );
                })}
              </div>
            )}

            <input
              type="text"
              value={form.emailTo}
              onChange={(e) => setForm({ ...form, emailTo: e.target.value })}
              className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
              placeholder="직접 입력 · 쉼표(,)로 구분"
            />
          </div>

          {/* 지금 기준으로 잡히는 공고 — 저장하기 전에 확인한다 */}
          <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
            <div className="flex items-center justify-between gap-3">
              <div>
                <div className="text-sm font-medium text-slate-700">지금 조건으로 잡히는 공고</div>
                              </div>
              <button
                type="button"
                onClick={runPreview}
                disabled={previewLoading}
                className="px-4 py-2 rounded-lg text-sm font-medium border border-slate-300 text-slate-700 hover:bg-white disabled:opacity-50 transition flex-shrink-0"
              >
                {previewLoading ? '확인 중...' : '확인'}
              </button>
            </div>

            {preview && (
              <div className="mt-3 pt-3 border-t border-slate-200">
                <div className="text-sm text-slate-700">
                  <span className="font-semibold text-slate-900">{preview.total}</span>건
                  {preview.total === 0 && <span className="ml-2 text-xs text-amber-600">메일 안 나감</span>}
                </div>
                <ul className="mt-2 space-y-1">
                  {preview.items.slice(0, 5).map((it) => (
                    <li key={it.bidNtceNo} className="text-xs text-slate-500 truncate">
                      · {it.bidNtceNm}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>

        <div className="px-6 py-4 border-t border-slate-100 flex justify-end gap-2 shrink-0 items-center">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm text-slate-600 hover:bg-slate-100 rounded-lg transition"
          >
            취소
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="px-5 py-2 bg-indigo-600 text-white rounded-lg text-sm font-medium hover:bg-indigo-700 disabled:opacity-40 disabled:cursor-not-allowed transition"
          >
            {saving ? '저장 중...' : editing ? '수정' : '추가'}
          </button>
        </div>
      </div>
    </div>
  );
};
